"use client"

import * as React from "react"
import { useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Workflow,
  FileText,
  Plug,
  ChevronRight,
  Settings,
  HelpCircle,
  Wallet,
  Building2,
  Users,
  Coins,
  Shield,
  Settings2,
  CreditCard,
  Lock,
  KeySquare,
  Tag,
  Gift,
  Receipt,
  List,
  Mail,
  ClipboardList,
} from "lucide-react"

import { ViewType } from "./Layout"
import { useTenant } from "@/contexts/TenantContext"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar"
import { Badge } from "@/components/ui/badge"

export interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  onNavigate: (view: ViewType) => void
  currentView: ViewType
}

// Workspace-scoped navigation items
const workspaceNavItems = [
  {
    id: "dashboard" as ViewType,
    title: "Dashboard",
    icon: LayoutDashboard,
  },
  {
    id: "workflows" as ViewType,
    title: "Workflows",
    icon: Workflow,
  },
  {
    id: "forms" as ViewType,
    title: "Forms",
    icon: ClipboardList,
  },
  {
    id: "logs" as ViewType,
    title: "Execution Logs",
    icon: FileText,
  },
  {
    id: "secrets" as ViewType,
    title: "Secrets",
    icon: Lock,
  },
  {
    id: "credentials" as ViewType,
    title: "Credentials",
    icon: KeySquare,
  }
]

// Tenant-scoped navigation items
const tenantNavItems = [
  {
    id: "plugins" as ViewType,
    title: "Plugins",
    icon: Plug,
  },
  {
    id: "billing" as ViewType,
    title: "Billing",
    icon: Wallet,
  },
  {
    id: "settings" as ViewType,
    title: "Settings",
    icon: Settings,
  },
]

// Platform admin navigation items - only tenant management views
const platformAdminNavItems = [
  {
    id: "admin-dashboard" as ViewType,
    title: "Admin Dashboard",
    icon: LayoutDashboard,
  },
  {
    id: "admin-tenants" as ViewType,
    title: "Organizations",
    icon: Building2,
  },
  {
    id: "admin-users" as ViewType,
    title: "Users",
    icon: Users,
  },
  {
    id: "admin-credits" as ViewType,
    title: "Credits Management",
    icon: Coins,
  },
  {
    id: "admin-billing-parameters" as ViewType,
    title: "Billing Parameters",
    icon: Settings2,
  },
  {
    id: "admin-plans" as ViewType,
    title: "Plans",
    icon: CreditCard,
  },
  {
    id: "admin-subscriptions" as ViewType,
    title: "Subscriptions",
    icon: List,
  },
  {
    id: "admin-invoices" as ViewType,
    title: "Invoices",
    icon: Receipt,
  },
  {
    id: "admin-plugins" as ViewType,
    title: "Plugins",
    icon: Plug,
  },
  {
    id: "admin-templates" as ViewType,
    title: "Templates",
    icon: FileText,
  },
  {
    id: "admin-promo-codes" as ViewType,
    title: "Promo Codes",
    icon: Tag,
  },
  {
    id: "admin-referral-codes" as ViewType,
    title: "Referral Codes",
    icon: Gift,
  },
  {
    id: "admin-email-audit-logs" as ViewType,
    title: "Email Audit Logs",
    icon: Mail,
  },
  {
    id: "admin-settings" as ViewType,
    title: "Platform Settings",
    icon: Settings,
  },
]

const secondaryItems = [
  {
    title: "Help",
    icon: HelpCircle,
    url: "#",
  },
]

export function AppSidebar({ onNavigate, currentView, ...props }: AppSidebarProps) {
  const { isPlatformAdmin, currentTenant } = useTenant()
  const userRole = currentTenant?.role
  const canAccessBilling = userRole === 'OWNER' || userRole === 'ADMIN'
  const visibleTenantNavItems = tenantNavItems.filter(
    item => item.id !== 'billing' || canAccessBilling
  )

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                {isPlatformAdmin ? <Shield className="size-4" /> : <Workflow className="size-4" />}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">Enveu</span>
                <span className="truncate text-xs text-muted-foreground">
                  {isPlatformAdmin ? "Platform Admin" : "Automation System"}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      
      <SidebarContent>
        {isPlatformAdmin && (
          <div className="px-3 py-2">
            <Badge variant="outline" className="w-full justify-center text-xs border-yellow-500/50 text-yellow-600 bg-yellow-500/10">
              <Shield className="h-3 w-3 mr-1" />
              Platform Admin Mode
            </Badge>
          </div>
        )}
        
        {isPlatformAdmin ? (
          <SidebarGroup>
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {platformAdminNavItems.map((item) => (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      tooltip={item.title}
                      isActive={currentView === item.id}
                      onClick={() => onNavigate(item.id)}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <>
            <SidebarGroup>
              <SidebarGroupLabel>Workspace</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {workspaceNavItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={currentView === item.id}
                        onClick={() => onNavigate(item.id)}
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup className="mt-auto">
              <SidebarGroupLabel>Admin Section</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleTenantNavItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={currentView === item.id}
                        onClick={() => onNavigate(item.id)}
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Support</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton tooltip={item.title} asChild>
                    <a href={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="sm" className="text-sidebar-foreground/70">
              <span className="text-xs">© 2026 Enveu</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      
      <SidebarRail />
    </Sidebar>
  )
}
